import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useRef, useState } from 'react';
import { FileText, Image as ImageIcon, Paperclip, Trash2, Upload } from 'lucide-react';
import { hardwareApi } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';

/**
 * NAMED ATTACHMENTS on a device — datasheets, certificates, wiring diagrams,
 * manuals, anything.
 *
 * THE NAME IS TYPED, NOT CHOSEN. A fixed set of document types cannot survive
 * contact with the next device: the model already carried a single
 * `datasheetUrl`, which turned out to be one slot for a kind of file that comes
 * in six kinds. So each row is a free-text name plus a file, and the list is
 * as long as it needs to be.
 *
 * Uploads reuse the SAME presign the photo gallery uses — hardware-service
 * derives the kind from the content type and applies that kind's size cap, so
 * this component never states a limit the service does not enforce.
 */

export interface DeviceDocument {
  name: string;
  url: string;
  contentType?: string;
  sizeBytes?: number;
  uploadedAt?: number;
}

const ACCEPTED = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
/** Mirrors hardware-service `LIMITS.doc`. */
const MAX_BYTES = 25 * 1024 * 1024;

const prettySize = (bytes?: number): string => {
  if (typeof bytes !== 'number' || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** The file's own name, minus the extension — a sane starting point to edit. */
const nameFromFile = (filename: string): string =>
  filename.replace(/\.[A-Za-z0-9]+$/, '').replace(/[-_]+/g, ' ').trim() || 'Document';

const DocumentsPanel: React.FC<{
  documents: DeviceDocument[];
  modelId?: string;
  onChange: (next: DeviceDocument[]) => void;
}> = ({ documents, modelId, onChange }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const upload = async (files: FileList | File[] | null) => {
    const list = files ? Array.from(files) : [];
    if (!list.length) return;
    setError(null);
    setBusy(true);
    const added: DeviceDocument[] = [];
    try {
      for (const file of list) {
        if (!ACCEPTED.includes(file.type)) {
          setError(`${file.name}: PDF or image only`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          setError(`${file.name}: over ${Math.round(MAX_BYTES / (1024 * 1024))} MB`);
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const { uploadUrl, fileUrl } = await hardwareApi.presignPhoto({
          modelId, filename: file.name, contentType: file.type, bytes: file.size,
        });
        // eslint-disable-next-line no-await-in-loop
        const put = await fetch(uploadUrl, {
          method: 'PUT', headers: { 'Content-Type': file.type }, body: file,
        });
        if (!put.ok) throw new Error(`upload failed (${put.status})`);
        added.push({
          name: nameFromFile(file.name),
          url: fileUrl,
          contentType: file.type,
          sizeBytes: file.size,
          uploadedAt: Date.now(),
        });
      }
      if (added.length) {
        onChange([...documents, ...added]);
        toastService.success(`${added.length} file${added.length === 1 ? '' : 's'} attached`);
      }
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const rename = (url: string, name: string) =>
    onChange(documents.map((d) => (d.url === url ? { ...d, name } : d)));

  const remove = async (url: string) => {
    // Detached immediately: being unable to remove a document from the device
    // is a real block, whereas a leftover S3 object is a minor cost.
    onChange(documents.filter((d) => d.url !== url));
    try {
      await hardwareApi.deletePhoto(url);
    } catch {
      toastService.warning('Detached, but the file could not be deleted from storage.');
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Documents</h3>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <Upload size={13} />}
          Attach file
        </button>
      </div>
      <p className="mb-3 text-[11px] text-gray-500">
        Datasheets, certificates, manuals — <b>you name each one</b>. There are no fixed document
        types, because the next device will want a kind nobody predicted. PDF or image.
      </p>

      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => upload(e.target.files)}
      />

      {error && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {documents.map((d) => {
          const isPdf = d.contentType === 'application/pdf';
          const Icon = isPdf ? FileText : ImageIcon;
          return (
            <div key={d.url} className="flex items-center gap-3 rounded-lg border border-gray-200 p-2.5">
              <span
                className={`grid h-8 w-8 flex-none place-items-center rounded-lg ${
                  isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                }`}
              >
                <Icon size={15} />
              </span>
              {/* The NAME is the editable part — the filename is evidence, not a label. */}
              <input
                value={d.name}
                onChange={(e) => rename(d.url, e.target.value)}
                placeholder="Name this document"
                className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-[13px] font-semibold text-gray-800 hover:border-gray-200 focus:border-gray-300 focus:bg-white focus:outline-none"
              />
              <a
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-[11px] text-gray-400 underline-offset-2 hover:text-gray-700 hover:underline"
              >
                {prettySize(d.sizeBytes) || 'open'}
              </a>
              <button
                type="button"
                onClick={() => remove(d.url)}
                title="Detach"
                className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
          className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed py-3 text-[12px] transition ${
            dragging
              ? 'border-red-400 bg-red-50 text-red-600'
              : 'border-gray-300 text-gray-400 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <Paperclip size={14} />
          {documents.length ? 'Attach another file' : 'Drop a file here, or click to attach'}
        </button>
      </div>
    </div>
  );
};

export default DocumentsPanel;
